import { ComponentType, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { IIconProps } from "@/components/icons/interface";
import { useAuthLayoutHook } from "@/layouts/customer-auth-layout/use-auth-layout-hook";
import { useUserPermission } from "@/hooks/use-permission";
import { effectivePermissionsState } from "@/store/permissions";
import { Permissions } from "@/components/CheckPermission/enum";
import { ProductsSettingsIcon } from "@/components/icons/products-settings-icon";
import { EcommerceIcon } from "@/components/icons/ecommerce-icon";
import { AutomationsIcon } from "@/components/icons/automations-icon";

/**
 * Modern-only "hub" pages. The rail collapses several menu entries into one link that
 * opens a page where the user picks the subsection. Groups are derived from the same
 * menu definition the classic sidebar uses, so titles and permissions never drift.
 */

export const REPORTS_HUB_PATH = "/reports";

/** Screens opened from the Reports hub. The modern top bar already titles these. */
export const REPORT_SCREEN_PATHS = [
  REPORTS_HUB_PATH,
  "/aging-report",
  "/ledger-report",
  "/daily-payments-report",
  "/sales-report",
  "/credit-card-transactions",
  "/vat-report",
  "/mailing-logs",
  "/machines-comparison",
  "/supplier-aging",
  "/supplier-ledger",
  "/payments-report",
  "/purchases-report",
  "/accounting-transaction-report",
  "/adjustments-report",
  "/transaction-journal-report",
  "/export-files",
] as const;

export const isReportScreen = (path?: string) => {
  const clean = (path || "").split("?")[0].replace(/\/$/, "") || "/";
  return (REPORT_SCREEN_PATHS as readonly string[]).includes(clean);
};
export const STUDIO_HUB_PATH = "/studio";

/** tabs2 groups folded into the "Reports" rail entry. */
export const REPORT_GROUP_KEYS = [
  "customerReports",
  "supplierReports",
  "accountingReports",
] as const;

/** tabs3 items folded into the "Studio" rail entry (everything but company reports). */
export const STUDIO_ITEM_KEYS = [
  "materials",
  "materialsAdmin",
  "machines",
  "machinesAdmin",
  "outputsAdmin",
  "parametersAdmin",
  "actionsAdmin",
  "actions",
] as const;

export const isReportGroupKey = (key?: string) =>
  !!key && (REPORT_GROUP_KEYS as readonly string[]).includes(key);

export const isStudioItemKey = (key?: string) =>
  !!key && (STUDIO_ITEM_KEYS as readonly string[]).includes(key);

/**
 * Icon component for hub cards. Accepts both the zero-arg menu icons and the sidebar
 * icons that take optional `IIconProps`; hubs always render it without props.
 */
export type HubIcon = ComponentType<IIconProps>;

export interface HubLink {
  key: string;
  title: string;
  path: string;
  icon?: HubIcon;
}

export interface HubGroup {
  key: string;
  title: string;
  links: HubLink[];
}

interface MenuTab {
  key?: string;
  title?: string;
  path?: string;
  isList?: boolean;
  list?: MenuTab[];
  icon?: () => JSX.Element;
  Permission?: Permissions;
}

/** Studio hub groups, in display order. */
export const STUDIO_GROUPS = [
  { key: "resources", title: "modernChrome.studioResources" },
  { key: "catalog", title: "settings.products" },
  { key: "automations", title: "modernChrome.studioAutomations" },
] as const;

type StudioGroupKey = (typeof STUDIO_GROUPS)[number]["key"];

interface StudioSettingsLink extends HubLink {
  /** `/settings/<route>` segment the page is served from. */
  route: string;
  group: StudioGroupKey;
  Permission?: Permissions;
}

/**
 * Settings sections that the modern theme moves into the Studio hub. Their pages keep
 * their `/settings/<route>` URLs (the widgets hard-code them for edit flows) but, in
 * modern, they are hidden from the Settings side list and render standalone.
 */
export const STUDIO_SETTINGS_LINKS: StudioSettingsLink[] = [
  {
    key: "products",
    route: "products",
    group: "catalog",
    title: "settings.products",
    path: "/settings/products",
    icon: ProductsSettingsIcon,
    Permission: Permissions.SHOW_PRODUCTS,
  },
  {
    key: "templates",
    route: "templates",
    group: "resources",
    title: "settings.templatesAdmin",
    path: "/settings/templates",
    icon: ProductsSettingsIcon,
    Permission: Permissions.MACHINES_ADMIN,
  },
  {
    key: "materialsSettings",
    route: "materials-settings",
    group: "resources",
    title: "materialsConfigAdmin.settingsTitle",
    path: "/materials-settings",
    icon: ProductsSettingsIcon,
    Permission: Permissions.MACHINES_ADMIN,
  },
  {
    key: "materialsActions",
    route: "materials-actions",
    group: "resources",
    title: "materialsConfigAdmin.actionsTitle",
    path: "/materials-actions",
    icon: ProductsSettingsIcon,
    Permission: Permissions.MACHINES_ADMIN,
  },
  {
    key: "materialsTranslations",
    route: "materials-translations",
    group: "resources",
    title: "materialsConfigAdmin.translationsTitle",
    path: "/materials-translations",
    icon: ProductsSettingsIcon,
    Permission: Permissions.MACHINES_ADMIN,
  },
  {
    key: "productsVariations",
    route: "products-variations",
    group: "catalog",
    title: "settings.productsVariations",
    path: "/settings/products-variations",
    icon: ProductsSettingsIcon,
    Permission: Permissions.SHOW_PRODUCTS_VARIATIONS,
  },
  {
    key: "ecommerce",
    route: "ecommerce",
    group: "catalog",
    title: "settings.ecommerce",
    path: "/settings/ecommerce",
    icon: EcommerceIcon,
    Permission: Permissions.SHOW_ECOMMERCE,
  },
  {
    key: "automations",
    route: "automations",
    group: "automations",
    title: "settings.automations",
    path: "/settings/automations",
    icon: AutomationsIcon,
    Permission: Permissions.SHOW_AUTOMATIONS,
  },
];

export const isStudioSettingsRoute = (route?: string) =>
  !!route && STUDIO_SETTINGS_LINKS.some((link) => link.route === route);

/** Absolute paths of the migrated settings pages (used for rail highlighting). */
export const STUDIO_SETTINGS_PATHS = STUDIO_SETTINGS_LINKS.map((link) => link.path);

const toLink = (tab: MenuTab): HubLink => ({
  key: tab.key as string,
  title: tab.title as string,
  path: tab.path as string,
  icon: tab.icon,
});

/** Customer / Supplier / Accounting report groups the current user may open. */
export const useReportGroups = (): HubGroup[] => {
  const { tabs2 } = useAuthLayoutHook(null, true);
  const { CheckPermission } = useUserPermission();
  // The permission list is loaded asynchronously; recompute when it changes.
  const permissions = useRecoilValue(effectivePermissionsState);

  return useMemo(
    () =>
      (tabs2 as MenuTab[])
        .filter((tab) => isReportGroupKey(tab.key))
        .map((tab) => ({
          key: tab.key as string,
          title: tab.title as string,
          links: (tab.list || [])
            .filter((item) => !item.Permission || CheckPermission(item.Permission))
            .map(toLink),
        }))
        .filter((group) => group.links.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tabs2, permissions],
  );
};

/**
 * Studio: production resources (materials, machines, actions, templates),
 * the product catalogue (products, variations, customer SKUs, e-commerce) and
 * automations.
 * Studio: production resources (materials, machines, actions and their admin views),
 * the product catalogue (products, variations, e-commerce) and automations.
 * Customer SKU management lives under Sales.
 */
export const useStudioGroups = (): HubGroup[] => {
  const { tabs3 } = useAuthLayoutHook(null, true);
  const { CheckPermission } = useUserPermission();
  // The permission list is loaded asynchronously; recompute when it changes.
  const permissions = useRecoilValue(effectivePermissionsState);

  return useMemo(() => {
    const allowed = (item: { Permission?: Permissions }) =>
      !item.Permission || CheckPermission(item.Permission);

    const resources = (tabs3 as MenuTab[])
      .filter((tab) => isStudioItemKey(tab.key) && allowed(tab))
      .map(toLink);

    const linksOf = (group: StudioGroupKey): HubLink[] =>
      STUDIO_SETTINGS_LINKS.filter((link) => link.group === group && allowed(link)).map(
        ({ key, title, path, icon }) => ({ key, title, path, icon }),
      );

    return STUDIO_GROUPS.map((group) => ({
      key: group.key,
      title: group.title,
      links:
        group.key === "resources"
          ? [...resources, ...linksOf("resources")]
          : linksOf(group.key),
    })).filter((group) => group.links.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs3, permissions]);
};
